import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import { ExpandableComponent } from 'components/expandable/Expandable'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import { CustomerForm } from 'constants/enums/formTypes'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { FormFactory } from 'formBuilder/formFactory'
import { formKeySelector, validateFormDataAction } from 'formBuilder/src'
import FormBuilder from 'formBuilder/src/components/FormBuilder'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { IApplicationInformation } from 'interfaces/i-application-information'
import React, { useContext, useEffect } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import updateApplicationServices from 'services/updateApplicationServices'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import avardaColors from 'common/styling/avardaColors.module.scss'

export const FillPersonalDataUK = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()

	const dispatch = useDispatch()

	// form name constants
	const PERSONAL_INFO_FORM = 'ApplicationPersonalInformationForm'
	const CONTACT_INFO_FORM = 'ApplicationContactInformationForm'
	const EMPLOYMENT_FORM = 'ApplicationEmploymentForm'
	const ADDRESS_FORM = 'ApplicationAddressForm'
	const FORMER_ADDRESS_FORM = 'ApplicationFormerAddressForm'
	const ACCOMMODATION_FORM = 'ApplicationAccommodationForm'

	// form schemas
	let personalInfoFormSchema = FormFactory(
		CustomerForm.ApplicationPersonalInformationForm,
		{
			formName: PERSONAL_INFO_FORM,
			blockName: PERSONAL_INFO_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	let contactInfoFormSchema = FormFactory(
		CustomerForm.ApplicationContactInformationForm,
		{
			formName: CONTACT_INFO_FORM,
			blockName: CONTACT_INFO_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	let employmentFormSchema = FormFactory(
		CustomerForm.ApplicationEmploymentForm,
		{
			formName: EMPLOYMENT_FORM,
			blockName: EMPLOYMENT_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	let addressFormSchema = FormFactory(
		CustomerForm.ApplicationAddressForm,
		{
			formName: ADDRESS_FORM,
			blockName: ADDRESS_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	let formerAddressFormSchema = FormFactory(
		CustomerForm.ApplicationFormerAddressForm,
		{
			formName: FORMER_ADDRESS_FORM,
			blockName: FORMER_ADDRESS_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)
	let accommodationFormSchema = FormFactory(
		CustomerForm.ApplicationAccommodationForm,
		{
			formName: ACCOMMODATION_FORM,
			blockName: ACCOMMODATION_FORM,
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation,
	)

	// form state values
	const personalInfoFormStateValues = useSelector(
		formKeySelector(
			personalInfoFormSchema.formMeta.formName,
			'controlsState',
		),
	)
	const contactInfoFormStateValues = useSelector(
		formKeySelector(
			contactInfoFormSchema.formMeta.formName,
			'controlsState',
		),
	)
	const employmentFormStateValues = useSelector(
		formKeySelector(
			employmentFormSchema.formMeta.formName,
			'controlsState',
		),
	)
	const addressFormStateValues = useSelector(
		formKeySelector(addressFormSchema.formMeta.formName, 'controlsState'),
	)
	const formerAddressFormStateValues = useSelector(
		formKeySelector(
			formerAddressFormSchema.formMeta.formName,
			'controlsState',
		),
	)
	const accommodationFormStateValues = useSelector(
		formKeySelector(
			accommodationFormSchema.formMeta.formName,
			'controlsState',
		),
	)

	// whenever any field is updated in the forms, dispatch the updateApplicationData on the loanApplication
	useEffect(() => {
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					applicant: {
						...loanApplication.updateApplicationData?.applicant,
						personalInfo: {
							...loanApplication.updateApplicationData?.applicant
								?.personalInfo,
							firstName:
								personalInfoFormStateValues?.firstName.value,
							lastName:
								personalInfoFormStateValues?.lastName.value,
							gender: personalInfoFormStateValues?.gender.value,
							title: personalInfoFormStateValues?.title.value,
							maritalStatus:
								personalInfoFormStateValues?.maritalStatus
									.value,
							birthDate:
								personalInfoFormStateValues?.birthDate.value,
							countryOfBirth:
								personalInfoFormStateValues?.countryOfBirth
									.value,
							cityOfBirth:
								personalInfoFormStateValues?.cityOfBirth.value,
							nationality:
								personalInfoFormStateValues?.nationality.value,
							citizenship: personalInfoFormStateValues
								?.citizenship.value
								? personalInfoFormStateValues.citizenship.value
										.split(',')
										.map((code: string) => code.trim())
								: [],
						},

						contacts: {
							...loanApplication.updateApplicationData?.applicant
								?.contacts,
							email: contactInfoFormStateValues?.email.value,
							cellPhone:
								contactInfoFormStateValues?.cellPhone.value,
						},
						employment: {
							...loanApplication.updateApplicationData?.applicant
								?.employment,
							occupationType:
								employmentFormStateValues?.occupationType.value,
							employedSince:
								employmentFormStateValues?.employedSince.value,
							employerName:
								employmentFormStateValues?.employerName.value,
							jobTitle: employmentFormStateValues?.jobTitle.value,
							industry: employmentFormStateValues?.industry.value,
							monthlyNetIncome:
								employmentFormStateValues?.monthlyNetIncome
									.value,
							otherIncome:
								employmentFormStateValues?.otherIncome.value,
						},
						address: {
							...loanApplication.updateApplicationData?.applicant
								?.address,
							streetNumber:
								addressFormStateValues?.streetNumber.value,
							street: addressFormStateValues?.street.value,
							flatNumber:
								addressFormStateValues?.flatNumber.value,
							buildingName:
								addressFormStateValues?.buildingName.value,
							zip: addressFormStateValues?.zip.value,
							city: addressFormStateValues?.city.value,
							countryOfResidence:
								addressFormStateValues?.countryOfResidence
									.value,
							timeAtAddress:
								addressFormStateValues?.timeAtAddress.value,
						},
						formerAddress: {
							...loanApplication.updateApplicationData?.applicant
								?.formerAddress,
							streetNumber:
								formerAddressFormStateValues?.formerStreetNumber
									.value,
							street: formerAddressFormStateValues?.formerStreet
								.value,
							flatNumber:
								formerAddressFormStateValues?.formerFlatNumber
									.value,
							buildingName:
								formerAddressFormStateValues?.formerBuildingName
									.value,
							zip: formerAddressFormStateValues?.formerZip.value,
							city: formerAddressFormStateValues?.formerCity
								.value,
							countryOfResidence:
								formerAddressFormStateValues
									?.formerCountryOfResidence.value,
						},
						accommodation: {
							...loanApplication.updateApplicationData?.applicant
								?.accommodation,
							type: accommodationFormStateValues?.accomodationType
								.value,
							cost: accommodationFormStateValues?.cost.value,
							childrenInFamily:
								accommodationFormStateValues?.childrenInFamily
									.value,
							childcareCost:
								accommodationFormStateValues?.childcareCost
									.value,
						},
					},
				},
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		personalInfoFormStateValues,
		contactInfoFormStateValues,
		employmentFormStateValues,
		addressFormStateValues,
		formerAddressFormStateValues,
		accommodationFormStateValues,
	])

	// once all forms have been defined, collapse them all
	const hasRunOnce = React.useRef(false)
	useEffect(() => {
		if (hasRunOnce.current) return

		const allFormValuesLoaded = [
			personalInfoFormStateValues,
			contactInfoFormStateValues,
			employmentFormStateValues,
			addressFormStateValues,
			formerAddressFormStateValues,
			accommodationFormStateValues,
		].every((value) => value !== undefined)

		if (!allFormValuesLoaded) return

		hasRunOnce.current = true

		collapseForm(PERSONAL_INFO_FORM)
		collapseForm(CONTACT_INFO_FORM)
		collapseForm(EMPLOYMENT_FORM)
		collapseForm(ADDRESS_FORM)
		collapseForm(FORMER_ADDRESS_FORM)
		collapseForm(ACCOMMODATION_FORM)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		personalInfoFormStateValues,
		contactInfoFormStateValues,
		employmentFormStateValues,
		addressFormStateValues,
		formerAddressFormStateValues,
		accommodationFormStateValues,
	])

	// collapses the form with the given id, if it's expanded
	function collapseForm(formId: string): void {
		const section = document.getElementById(formId)
		if (!section) return

		const headerChild = (Array.from(section.children) as HTMLElement[])[0]
		if (!headerChild) return

		const isExpanded = headerChild.classList.contains('expanded')
		if (isExpanded) headerChild.click()
	}

	// expands the form with given id if it has errors and returns true
	function expandSectionIfFormHasErrors(formId: string): boolean {
		// Find the section div by id
		const section = document.getElementById(formId)
		if (!section) {
			return true
		}

		// Assumes exactly two direct child divs:
		// [0] = headerChild, [1] = formChild
		const [headerChild, formChild] = Array.from(
			section.children,
		) as HTMLElement[]
		if (!headerChild || !formChild) {
			return true
		}

		// Check if any field in the form has errors
		const hasError = formChild.querySelector('.has-error') !== null

		// If there is an error and section is not expanded, simulate click
		const isExpanded = headerChild.classList.contains('expanded')
		if (hasError && !isExpanded) {
			headerChild.click()
		}

		return hasError
	}

	// call cda/px with the updated data
	const submitApplicationData = async () => {
		// validate all form data
		if (personalInfoFormStateValues)
			dispatch(validateFormDataAction(PERSONAL_INFO_FORM))
		if (contactInfoFormStateValues)
			dispatch(validateFormDataAction(CONTACT_INFO_FORM))
		if (employmentFormStateValues)
			dispatch(validateFormDataAction(EMPLOYMENT_FORM))
		if (addressFormStateValues)
			dispatch(validateFormDataAction(ADDRESS_FORM))
		if (formerAddressFormStateValues)
			dispatch(validateFormDataAction(FORMER_ADDRESS_FORM))
		if (accommodationFormStateValues)
			dispatch(validateFormDataAction(ACCOMMODATION_FORM))

		// expand all the forms that have errors, and don't proceed to send data to cda/px if at least one form has errors
		const hasErrors = [
			expandSectionIfFormHasErrors(PERSONAL_INFO_FORM),
			expandSectionIfFormHasErrors(CONTACT_INFO_FORM),
			expandSectionIfFormHasErrors(EMPLOYMENT_FORM),
			expandSectionIfFormHasErrors(ADDRESS_FORM),
			expandSectionIfFormHasErrors(FORMER_ADDRESS_FORM),
			expandSectionIfFormHasErrors(ACCOMMODATION_FORM),
		].some((error) => error)
		if (hasErrors) return

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.updateApplicationUK(
					loanApplication.applicationData!,
					loanApplication.updateApplicationData!,
				)
				.then(async (response) => {
					if (response.status === 200) {
						dispatchMsp({
							type: MSP_ACTIONS.SET_INDEX,
							payload: {
								index: msp.index + 1,
							},
						})
					}
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	const colour1 = avardaColors.primaryDeepGreen
	const colour2 = avardaColors.primaryPrimeGreen
	const getColour = (key: number) => (key % 2 === 0 ? colour1 : colour2)

	const createFormSection = (
		key: number,
		id: string,
		content: string,
		formSchema: any,
	) => (
		<ExpandableComponent
			key={key}
			id={id}
			children={
				<div className="update-personal-data-section container-collapse">
					{formSchema ? (
						<FormBuilder
							formSchema={formSchema}
							apiError={undefined}
							showErrorsInForm={true}
							showSubmitButton={false}
						/>
					) : (
						<CircleSpinner />
					)}
				</div>
			}
			onClick={() => {}}
			content={content}
			backgroundColor={avardaColors.primaryBrightGreen}
			leftBorderColour={getColour(key)}
			expanded={true}
		/>
	)

	return (
		<div className="update-personal-data">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : error ? (
				<ErrorBox message={t('ErrorMsg')} />
			) : (
				<>
					{createFormSection(
						1,
						PERSONAL_INFO_FORM,
						t('PersonalInformationHeader'),
						personalInfoFormSchema,
					)}
					{createFormSection(
						2,
						CONTACT_INFO_FORM,
						t('ContactInfoHeader'),
						contactInfoFormSchema,
					)}
					{createFormSection(
						3,
						EMPLOYMENT_FORM,
						t('EmploymentAndIncomeHeader'),
						employmentFormSchema,
					)}
					{createFormSection(
						4,
						ADDRESS_FORM,
						t('AddressHeader'),
						addressFormSchema,
					)}
					{createFormSection(
						5,
						FORMER_ADDRESS_FORM,
						t('FormerAddressHeader'),
						formerAddressFormSchema,
					)}
					{createFormSection(
						6,
						ACCOMMODATION_FORM,
						t('AccommodationHeader'),
						accommodationFormSchema,
					)}
					<div className="update-personal-data-next-button">
						<button
							type="button"
							disabled={loading}
							className="btn btn-primary"
							onClick={() => {
								setTimeout(() => {
									submitApplicationData()
								}, 400)
							}}>
							{t('Continue')}
						</button>
					</div>
				</>
			)}
		</div>
	)
}
